/**
 * Founder Mission Control: one projection for repository home and Task story.
 * Components must not invent a second status or next action.
 */
import { TASK_LABEL } from "./taskCopy";
import { conciseTaskSummary, customerStatusCopy } from "./taskCustomerCopy";
import { projectTaskWorkspace } from "./taskWorkspaceProjection";
import { taskShortTitle, taskRequestFingerprint, isEvaluationTask } from "./taskShortTitle";
import { deliveredWorkFromReview, founderSourceAction } from "./deliveredWorkDisplay";

export const FOUNDER_JOURNEY = [
  { id: "conversation", label: "Conversation", section: "conversation" },
  { id: "task", label: "Task", section: "task" },
  { id: "engineering", label: "Engineering", section: "engineering" },
  { id: "delivery", label: "Delivery", section: "delivery" },
  { id: "review", label: "Review", section: "review" },
];

export function founderHero(projection = {}, requestText = "", review = null, repo = {}, plan = null) {
  const copy = customerStatusCopy(projection.stage, projection.status);
  const work = deliveredWorkFromReview(review, repo, plan);
  const stale = projection.status === "Stale";
  const sourceAction = founderSourceAction(work);
  const needsConnect = stale ? !work.known : projection.stage === "Ready for review" && !work.known;
  return {
    headline: stale
      ? work.known
        ? "The latest work needs review"
        : "Choose the latest work"
      : copy.statusLabel,
    explanation: stale
      ? work.known
        ? "The work changed after the last review. The previous review no longer describes the latest version."
        : "Paste the pull request or branch your engineer sent you so RenderRepo can review the right work."
      : copy.statusExplanation,
    ownerLabel: copy.owner?.label || projection.owner?.label || "someone",
    ownerKind: copy.owner?.role === "product" ? "You decide" : copy.owner?.role === "engineering" ? "Engineering builds" : copy.owner?.role === "reviewer" || copy.owner?.role === "administrator" ? "A reviewer approves" : "AI prepares",
    primaryAction: needsConnect
      ? { id: "connect_delivered_work", label: "Connect delivered work", section: "delivery" }
      : stale
        ? sourceAction
        : copy.primaryAction?.id === "select_branch"
          ? { id: "connect_delivered_work", label: "Connect delivered work", section: "delivery" }
          : copy.primaryAction,
    outcome: conciseTaskSummary(requestText),
    shortTitle: taskShortTitle(requestText),
    deliveredWork: work,
    verified: true,
  };
}

export function founderAiGuide(projection = {}, review = null, repo = {}, plan = null) {
  const work = deliveredWorkFromReview(review, repo, plan);
  const stale = projection.status === "Stale";
  const blocked = projection.status === "Blocked" && projection.stage === "Waiting for decision";
  const planning = projection.stage === "Planning";
  const needsConnect = (stale && !work.known) || (projection.stage === "Ready for review" && !work.known);
  if (needsConnect) {
    return {
      kind: "guide",
      done: ["I compared the original request, your planning notes, and the repository."],
      needs: "Paste the pull-request link from your engineer. I’ll compare only what changed with the Task you approved.",
      next: "After you confirm, I’ll start the review and show what is proven.",
      chips: [
        { id: "where_branch", label: "Where do I add the branch?" },
        { id: "which_branch", label: "Which branch is connected?" },
        { id: "ask_engineer", label: "What should I ask my engineer for?" },
      ],
      trust: "Suggested by AI",
      focusInput: true,
    };
  }
  if (stale) {
    return {
      kind: "guide",
      done: [
        "I compared the original request, your planning notes, and the repository.",
        "I already reviewed an earlier version of the work.",
      ],
      needs: work.label
        ? `The connected work is ${work.label}. Confirm that we should review the latest version.`
        : "Confirm the latest delivered work so I can review it.",
      next: "I will check that work against the agreed Task and explain what is proven.",
      chips: [
        { id: "what_reviewed", label: "What exactly will be reviewed?" },
        { id: "why_again", label: "Why does the review need to run again?" },
        { id: "who", label: "Who makes the final decision?" },
      ],
      trust: "Suggested by AI, using verified Task status",
    };
  }
  if (blocked) {
    return {
      kind: "guide",
      done: ["I reviewed the delivered work against the agreed Task."],
      needs: "The latest code has not passed its required tests, so you cannot approve yet.",
      next: "I can explain what still needs proof after the tests are connected.",
      chips: [
        { id: "why_blocked", label: "Why can’t I approve this?" },
        { id: "who", label: "Who needs to act?" },
        { id: "unproven", label: "What is still unproven?" },
      ],
      trust: "Verified system state, explained by AI",
    };
  }
  if (planning) {
    return {
      kind: "guide",
      done: ["I read your request and checked the repository for important decisions."],
      needs: "Answer the current product question so the Task can stay precise.",
      next: "I will update the living Task after you confirm.",
      chips: [
        { id: "recommend", label: "Recommend an answer" },
        { id: "why", label: "Why does this matter?" },
        { id: "repo", label: "Does the repository answer this?" },
      ],
      trust: "Suggested by AI",
    };
  }
  return {
    kind: "guide",
    done: ["I am tracking this Task against the agreed outcome."],
    needs: projection.statusExplanation || "Continue from the current step.",
    next: projection.whatHappensNext || "I will keep the next step visible.",
    chips: [
      { id: "next", label: "What should happen next?" },
      { id: "who", label: "Who needs to act?" },
    ],
    trust: "Suggested by AI, using verified Task status",
  };
}

export function founderUnrelatedRequirements(requestText = "") {
  const text = String(requestText || "");
  if (/schema|letter case|content-type|content type/i.test(text)) {
    return {
      items: [
        "Personal data handling",
        "Retention limits",
        "File-size limits",
        "Sensitive-data rules",
      ],
      why: "These look like generic safety templates. They do not describe this schema-key behavior.",
    };
  }
  return null;
}

export function founderGlance(requestText = "") {
  const text = String(requestText || "");
  const unrelated = founderUnrelatedRequirements(text);
  if (/schema|letter case|content-type|content type/i.test(text)) {
    return {
      outcome: "Treat request schema keys consistently regardless of letter case.",
      inScope: [
        "Request schemas",
        "Invalid duplicate handling",
        "Existing lowercase compatibility",
        "Types, documentation, and tests",
      ],
      notIncluded: [
        "Response-schema behavior unless you already decided it is in scope",
        "Unrelated data-retention or PII policies",
      ],
      unrelated,
      success:
        "Existing integrations keep working, invalid duplicates fail clearly, and the relevant tests pass.",
    };
  }
  return {
    outcome: conciseTaskSummary(text),
    inScope: ["The behavior described in this Task"],
    notIncluded: ["Work you have not asked for"],
    unrelated,
    success: "The delivered work matches this Task and can be checked.",
  };
}

export function founderAiProposal(projection = {}, requestText = "") {
  if (projection.status === "Stale") {
    return {
      id: "rerun_review",
      before: "AI suggests reviewing the latest work.",
      evidence:
        "A review already exists, and the code changed after it finished. The agreed Task did not change.",
      change:
        "RenderRepo will check the current work against this Task. The old review will not be used to decide. Your plan stays intact.",
      confirmation: "You choose when this starts. AI does not decide.",
      confirmLabel: "Review latest version",
      declineLabel: "Not now",
      section: "review",
    };
  }
  const unrelated = founderUnrelatedRequirements(requestText);
  if (unrelated) {
    return {
      id: "classify_unrelated",
      before: `AI suggests these ${unrelated.items.length} requirements appear unrelated to this feature.`,
      evidence: unrelated.why,
      change:
        "They would move to “Possibly unrelated” on the next Task version. The frozen Task stays unchanged until you confirm a new version.",
      confirmation: "You decide whether they belong. AI will not remove them on its own.",
      confirmLabel: "Mark as unrelated later",
      declineLabel: "Keep them for now",
      section: "plan",
    };
  }
  return null;
}

export function founderJourney(projection = {}, review = null, repo = {}, plan = null) {
  const stage = projection.stage;
  const stale = projection.status === "Stale";
  const work = deliveredWorkFromReview(review, repo, plan);
  const states = {
    conversation: "upcoming",
    task: "upcoming",
    engineering: "upcoming",
    delivery: "upcoming",
    review: "upcoming",
  };
  if (["Ready for handoff", "Waiting for engineering", "In progress", "Ready for review", "Waiting for decision", "Completed", "Needs attention"].includes(stage)) {
    states.conversation = "complete";
    states.task = "complete";
  } else if (stage === "Planning") {
    states.conversation = "current";
    states.task = "upcoming";
  }
  if (["In progress", "Ready for review", "Waiting for decision", "Completed", "Needs attention"].includes(stage)) {
    states.engineering = "complete";
  } else if (["Ready for handoff", "Waiting for engineering"].includes(stage)) {
    states.engineering = "current";
  }
  if (work.known && !stale) states.delivery = "complete";
  else if (["Ready for review", "Needs attention"].includes(stage) || stale) states.delivery = "current";
  if (stage === "Completed") {
    states.review = "complete";
  } else if (stale) {
    states.review = "needs_attention";
  } else if (stage === "Waiting for decision") {
    states.review = projection.status === "Blocked" ? "waiting" : "current";
  } else if (["Ready for review", "Needs attention"].includes(stage)) {
    states.review = work.known ? "current" : "upcoming";
  }
  return FOUNDER_JOURNEY.map((step) => ({
    ...step,
    state: states[step.id] || "upcoming",
  }));
}

export function answerFounderPrompt(prompt, { projection = {}, requestText = "", review = null, repo = {} } = {}) {
  const q = String(prompt || "").toLowerCase();
  const hero = founderHero(projection, requestText, review, repo);
  if (/where do i add|add the branch|paste/.test(q)) {
    return {
      kind: "verified",
      text: "Paste it under Connect delivered work.",
      sources: ["Current Task"],
      focusInput: true,
    };
  }
  if (/which branch is connected|connected/.test(q)) {
    return {
      kind: "verified",
      text: hero.deliveredWork?.label
        ? `The connected work is ${hero.deliveredWork.label}.`
        : "No delivered work is connected yet. Paste the pull-request or branch link.",
      sources: ["Current review source"],
      focusInput: !hero.deliveredWork?.known,
    };
  }
  if (/what exactly will be reviewed|what will be reviewed/.test(q)) {
    return {
      kind: "verified",
      text: hero.deliveredWork?.label
        ? `RenderRepo will compare ${hero.deliveredWork.label} with this Task.`
        : "After you paste a pull request or branch, you’ll see exactly what will be reviewed before any credits are used.",
      sources: ["Delivered work"],
    };
  }
  if (/will this change my task/.test(q)) {
    return {
      kind: "verified",
      text: "No. Connecting delivered work does not change the agreed Task.",
      sources: ["Task freeze"],
    };
  }
  if (/will i be charged|credits/.test(q)) {
    return {
      kind: "verified",
      text: "No credits are used until you confirm Review this work. Check this work only resolves the source.",
      sources: ["Credit confirmation"],
    };
  }
  if (/why does the review need to run again|run again/.test(q)) {
    return {
      kind: "verified",
      text: "The work changed after the last review. The previous review no longer describes the latest version.",
      sources: ["Current review status"],
    };
  }
  if (/who makes the final decision/.test(q)) {
    return {
      kind: "verified",
      text: "A person records the final decision. AI prepares the review and does not decide.",
      sources: ["Decision policy"],
    };
  }
  if (/ask my engineer|don.?t have a link/.test(q)) {
    return {
      kind: "suggestion",
      text: `Ask your engineer for the GitHub pull-request or branch link for: ${hero.shortTitle}.`,
      sources: ["Task title"],
    };
  }
  if (/why can.?t i approve|approve this/.test(q)) {
    return {
      kind: "verified",
      text:
        projection.status === "Stale"
          ? "The work changed after the last review. The previous review no longer describes the latest version."
          : hero.explanation,
      sources: ["Current Task status"],
    };
  }
  if (/who needs to act|who is waiting|owner/.test(q)) {
    return {
      kind: "verified",
      text: `Waiting on ${hero.ownerLabel}. ${projection.whatHappensNext || ""}`.trim(),
      sources: ["Current Task owner"],
    };
  }
  if (/still unproven|unproven|missing proof/.test(q)) {
    const blockers = (projection.blockers || []).map((item) => item.text).slice(0, 3);
    return {
      kind: "verified",
      text: blockers.length
        ? blockers.join(" ")
        : "Open the review to see which requirements still need proof.",
      sources: ["Current review blockers"],
    };
  }
  if (/explain|recommendation|without technical/.test(q)) {
    return {
      kind: "suggestion",
      text: `${hero.headline}. ${hero.explanation}`,
      sources: ["Task status and latest review"],
    };
  }
  if (/happen next|should i do/.test(q)) {
    return {
      kind: "verified",
      text: projection.whatHappensNext || hero.explanation,
      sources: ["Current Task status"],
    };
  }
  return {
    kind: "suggestion",
    text: `${hero.explanation} ${projection.whatHappensNext || ""}`.trim(),
    sources: ["Current Task status"],
  };
}

function unpackMissionReview(entry) {
  if (!entry) return { review: null, snapshot: null };
  if (entry.review || entry.snapshot) {
    return { review: entry.review || null, snapshot: entry.snapshot || null };
  }
  return { review: entry, snapshot: null };
}

function storyFromPlan(plan, reviewEntry) {
  const { review, snapshot } = unpackMissionReview(reviewEntry);
  const projection = projectTaskWorkspace({
    planStatus: plan.status,
    planError: plan.error,
    latestReview: review || null,
    orchestrationSnapshot: snapshot,
    hasEngAck: String(plan.status || "").toLowerCase() === "completed",
    hasDraft: Boolean(plan.acceptanceCriteria?.length || plan.planIntelligence),
  });
  const hero = founderHero(projection, plan.featureRequestText, review, {}, plan);
  const needsFounder =
    projection.status === "Stale" ||
    projection.stage === "Planning" ||
    projection.stage === "Ready for handoff" ||
    projection.stage === "Ready for review" ||
    (projection.stage === "Waiting for decision" && projection.status !== "Blocked");
  return {
    id: plan._id,
    title: hero.shortTitle || taskShortTitle(plan.featureRequestText || ""),
    fingerprint: taskRequestFingerprint(plan.featureRequestText),
    evaluation: isEvaluationTask(plan),
    statusLabel: hero.headline,
    explanation: hero.explanation,
    ownerLabel: hero.ownerLabel,
    primaryAction: hero.primaryAction,
    stage: projection.stage,
    needsFounder,
    completed: projection.stage === "Completed",
    updatedAt: plan.updatedAt,
    projection,
  };
}

function founderSafePurpose(text) {
  const value = String(text || "").trim();
  if (!value || /unable to generate|due to an error/i.test(value)) return null;
  return value;
}

export function projectRepositoryMission({
  repo = {},
  plans = [],
  reviewsByPlanId = {},
  loading = false,
  reviewsReady = true,
} = {}) {
  if (loading) {
    return {
      repoName: repo.owner && repo.name ? `${repo.owner}/${repo.name}` : "Repository",
      purpose: founderSafePurpose(repo.description || repo.purpose),
      knowledgeReady: String(repo.status || "").toLowerCase() === "completed",
      fileCount: null,
      activeCount: 0,
      attentionCount: 0,
      loading: true,
      reviewsReady: false,
      briefing: {
        observation: "Checking what is moving in this repository.",
        verified: false,
        recommendation: "Status will appear as soon as the latest Task data is ready.",
      },
      needsYou: [],
      active: [],
      completed: [],
      allTasks: [],
    };
  }
  const stories = (plans || [])
    .filter((plan) => !plan.founderHidden && !plan.archivedAt)
    .map((plan) => storyFromPlan(plan, reviewsByPlanId[plan._id]))
    .filter((item) => !item.evaluation);
  const grouped = new Map();
  stories.forEach((item) => {
    const key = item.fingerprint || item.id;
    const current = grouped.get(key);
    if (!current) grouped.set(key, [item]);
    else current.push(item);
  });
  const possibleDuplicates = [];
  const uniqueStories = [];
  grouped.forEach((group) => {
    const sorted = [...group].sort(
      (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
    );
    uniqueStories.push(sorted[0]);
    if (sorted.length > 1) {
      possibleDuplicates.push({
        title: sorted[0].title,
        count: sorted.length,
        ids: sorted.map((item) => item.id),
      });
    }
  });
  const attentionRank = (item) => {
    if (item.projection.status === "Stale") return 1;
    if (item.stage === "Waiting for decision" && item.projection.status !== "Blocked") return 2;
    if (item.stage === "Planning") return 3;
    if (item.stage === "Ready for review") return 4;
    if (item.stage === "Ready for handoff") return 5;
    return 9;
  };
  const needsYou = uniqueStories
    .filter((item) => item.needsFounder)
    .sort((a, b) => attentionRank(a) - attentionRank(b) || new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
    .slice(0, 3)
    .map((item, index) => ({
      ...item,
      whyFirst:
        index === 0 && item.projection.status === "Stale"
          ? "Start here. This review is out of date, so no decision can be made until the latest work is checked."
          : index === 0
            ? "Start here."
            : null,
    }));
  const neededIds = new Set(needsYou.map((item) => item.id));
  const active = uniqueStories
    .filter((item) => !item.completed && !neededIds.has(item.id))
    .slice(0, 5);
  const completed = uniqueStories.filter((item) => item.completed).slice(0, 4);
  const attentionCount = needsYou.length;
  const movingCount = uniqueStories.filter((item) => !item.completed && item.stage !== "Planning").length;
  const staleCount = uniqueStories.filter((item) => item.projection.status === "Stale").length;
  const moving =
    movingCount > 0
      ? `${movingCount} Task${movingCount === 1 ? " is" : "s are"} moving forward.`
      : "No Tasks are in engineering or review.";
  let observation = `${moving} Nothing else needs your attention.`;
  if (staleCount === 1 && attentionCount === 1) {
    observation = `${moving} One review needs you to check the latest work. Nothing else needs your attention.`;
  } else if (attentionCount === 1) {
    observation = `${moving} One Task needs your attention.`;
  } else if (attentionCount > 1) {
    observation = `${moving} ${attentionCount} Tasks need your attention.`;
  }
  if (!reviewsReady) {
    observation = `${moving} Checking whether any reviews need you.`;
  }
  return {
    repoName: repo.owner && repo.name ? `${repo.owner}/${repo.name}` : "Repository",
    purpose: founderSafePurpose(repo.description || repo.purpose),
    loading: false,
    reviewsReady,
    knowledgeReady: String(repo.status || "").toLowerCase() === "completed",
    fileCount: repo.fileCount || repo.analysisCoverage?.filesIndexed || null,
    activeCount: stories.filter((item) => !item.completed).length,
    attentionCount,
    briefing: {
      observation,
      verified: reviewsReady,
      recommendation: needsYou[0]
        ? needsYou[0].whyFirst || `Start with: ${needsYou[0].statusLabel}.`
        : `Start a ${TASK_LABEL} when you have a new idea.`,
    },
    needsYou,
    active,
    completed,
    possibleDuplicates,
    allTasks: stories
      .slice()
      .sort((a, b) => {
        const aNeed = a.needsFounder ? 0 : a.completed ? 2 : 1;
        const bNeed = b.needsFounder ? 0 : b.completed ? 2 : 1;
        if (aNeed !== bNeed) return aNeed - bNeed;
        if (aNeed === 0) {
          return (
            attentionRank(a) - attentionRank(b) ||
            new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
          );
        }
        return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
      })
      .map((item) => ({
        ...item,
        whyFirst: needsYou[0] && item.id === needsYou[0].id ? needsYou[0].whyFirst : null,
      })),
  };
}
